/* Shared config for the digital Trade Appraisal Certificate —
   used by the public form AND the advisor Trades tab detail view. */

export const VEHICLE_TYPES = [
  'TT', 'TTTH', 'Hybrid', 'FW', 'FWTH', 'Park Model',
  'Class A', 'Class AD', 'Class B', 'Class C', 'Pop Up', 'Truck Camper',
];

export const MOTORIZED_TYPES = ['Class A', 'Class AD', 'Class B', 'Class C'];

export const MOTORIZED_FIELDS = [
  { key: 'chassis_type', label: 'Chassis Type' },
  { key: 'chassis_make', label: 'Chassis Make' },
  { key: 'chassis_year', label: 'Chassis Year' },
  { key: 'generator', label: 'Generator Type / Size / Hours' },
  { key: 'engine_type', label: 'Engine Type' },
  { key: 'hp', label: 'HP' },
  { key: 'transmission', label: 'Transmission' },
  { key: 'odometer', label: 'Odometer Reading' },
  { key: 'last_gen_oil', label: 'Last Generator Oil Change' },
  { key: 'last_engine_oil', label: 'Last Engine Oil Change' },
];

export const MOTORIZED_CHOICES = [
  { key: 'actual_miles', label: 'Actual Miles?', options: ['Yes', 'No'] },
  { key: 'dash_ac', label: 'Dash A/C Working?', options: ['Yes', 'No'] },
  { key: 'windshield', label: 'Windshield Condition', options: ['Good', 'Stars', 'Holes', 'Cracks'] },
  { key: 'paint_fabric_protection', label: 'Paint & Fabric Protection?', options: ['Yes', 'No'] },
  { key: 'service_agreement', label: 'Service Agreement / Warranty?', options: ['Yes', 'No'] },
];

export const CONDITION_QUESTIONS = [
  { key: 'hazard_lights', label: 'Any hazard lights currently showing on dash?', only: 'motorized' },
  { key: 'exhaust_leaks', label: 'Leaks in exhaust system, including manifolds?', only: 'motorized' },
  { key: 'delamination', label: 'Is there any delaminating (separation) on the unit?' },
  { key: 'exterior_damage', label: 'Is there any damage to the exterior?', describe: 'Describe' },
  { key: 'water_damage', label: 'Has the unit ever leaked or had water damage?', describe: 'Describe' },
  { key: 'floor_soft', label: 'Does the floor have any soft spots / areas of concern?', describe: 'Where?' },
  { key: 'roof_soft', label: 'Does the roof have any soft spots / areas of concern?', describe: 'Describe' },
  { key: 'faded_paint', label: 'Is the front cap or paint faded anywhere on the RV?', describe: 'Describe' },
  { key: 'modified', label: 'Have you modified the RV / furniture / floors?' },
  { key: 'stains', label: 'Any stains in the upholstery or carpet?' },
  { key: 'dry_rot', label: 'Any dry rot?' },
  { key: 'smoked', label: 'Smoked in?' },
  { key: 'pets', label: 'Pets?' },
  { key: 'accident', label: 'Ever been in an accident, hail storm, fire, or flood?' },
];

export const CONDITION_ITEMS = [
  { key: 'air_conditioners', label: 'Air Conditioner(s)', hint: 'Ever serviced? How many?' },
  { key: 'automotive_instruments', label: 'Automotive Instruments', hint: 'All working fine?', only: 'motorized' },
  { key: 'awning', label: 'Awning and Awning Fabric', hint: 'Any rips or tears?' },
  { key: 'bathroom', label: 'Bathroom — Tub and Shower', hint: 'Any leaks or damage?' },
  { key: 'batteries', label: 'Batteries', hint: 'Number? Age? Ever replaced?' },
  { key: 'brakes_axles', label: 'Brakes / Axles', hint: 'Ever replaced? Or recalls?', only: 'towable' },
  { key: 'beds_upholstery', label: 'Beds and Upholstery', hint: 'Any rips, tears, discoloration?' },
  { key: 'walls_ceiling', label: 'Walls and Ceiling', hint: 'Holes? Torn wallpaper?' },
  { key: 'stove_oven', label: 'Stove and Oven', hint: 'Original oven?' },
  { key: 'cruise_control', label: 'Cruise Control', hint: 'Working?', only: 'motorized' },
  { key: 'drapes', label: 'Drapes, Shades, Blinds, Curtains', hint: 'Need restrung? Broken? Stained?' },
  { key: 'engine_drivetrain', label: 'Engine / Drive Train', hint: 'Is it performing properly?', only: 'motorized' },
  { key: 'exterior_paint', label: 'Exterior Paint and Body', hint: 'Any dents / dings / scratches?' },
  { key: 'floor', label: 'Floor — Carpet, Vinyl, Tile', hint: 'Any rips / tears / cracks?' },
  { key: 'furnace', label: 'Furnace', hint: 'Blowing hot air?' },
  { key: 'holding_tanks', label: 'Holding Tanks', hint: 'Please dump your tanks before drop-off!' },
  { key: 'lp_system', label: 'LP System and Tank', hint: 'Any known leaks?' },
  { key: 'microwave', label: 'Microwave', hint: 'Ever been replaced?' },
  { key: 'power_converter', label: 'Power Converter', hint: 'Any bad fuses or electrical issues?' },
  { key: 'steps', label: 'Power Step / Entry Steps', hint: 'Bent / damaged / needing repair?' },
  { key: 'rear_camera', label: 'Rear View Camera', hint: 'Working?' },
  { key: 'refrigerator', label: 'Refrigerator', hint: 'Working on both electric and gas?' },
  { key: 'slide_toppers', label: 'Slide Toppers', hint: 'Any rips or tears?' },
  { key: 'roof', label: 'Roof and Roof Sealant', hint: 'Resealed or treated annually? When last?' },
  { key: 'stabilizer_jacks', label: 'Stabilizer / Leveling Jacks', hint: 'Ever bent? Missing?' },
  { key: 'toilet_pump', label: 'Toilet / Water Pump', hint: 'Water pump ever replaced?' },
  { key: 'tires', label: 'Tire Condition and Age', hint: 'Original or new?' },
  { key: 'tv_antenna', label: 'TV Antenna', hint: 'Broken / bent / missing?' },
  { key: 'av_equipment', label: 'TV, DVD, Surround Sound', hint: 'Any damage or electronics not working?' },
  { key: 'satellite', label: 'Satellite', hint: 'Rooftop satellite working?' },
  { key: 'water_heater', label: 'Water Heater', hint: 'Last serviced when?' },
  { key: 'water_system', label: 'Water System and Water Lines', hint: 'Kept winterized?' },
  { key: 'windows', label: 'Windows / Glass', hint: 'Any fogged or broken glass?' },
  { key: 'wipers_lights', label: 'Wipers / Turn Signals / Running Lights', hint: 'Any items not working?', only: 'motorized' },
];

// Which questions/items apply to the selected vehicle type.
export const isRelevant = (entry, vehicleType) => {
  if (!entry.only) return true;
  const motorized = MOTORIZED_TYPES.includes(vehicleType);
  return entry.only === 'motorized' ? motorized : !motorized;
};

export const ITEM_RATINGS = ['Excellent', 'Good', 'Poor', 'N/A'];

export const CERT_TEXT =
  'I certify that the information I have given on this form is true and accurate to the best of my knowledge. ' +
  'I understand that the dealership is relying upon this information and is basing their value of the vehicle on its accuracy. ' +
  'I agree that the value of this vehicle may be adjusted downward if the description I have given is not correct, ' +
  'based on findings in the trade inspection.';

export const INSPECTION_TEXT =
  'I understand a one (1) hour check will be performed by a tech before the walk-thru.';
